import type { WizardPersistedSnapshot } from '@heidi/flow/wizard';

import type { OnboardingFlowKind, PersonalizationStep } from '../../../contracts';
import { personalizationWizard } from '../personalization.wizard';

/**
 * Onboarding progress survives a page refresh under the `sessionStorage` key
 * `heidi-onboarding:personalization-progress:v1` — not `localStorage`: the
 * record holds the clinician's own profile details (name, phone, clinic) and a
 * tab-scoped store is discarded when the tab closes, so an abandoned signup on
 * a shared machine leaves nothing behind.
 *
 * That key is repeated inline at each storage call below rather than held in a
 * constant, because `scripts/generate-persistence-manifest.ts` records raw Web
 * Storage keys only where it can read them as literals at the call site — a key
 * behind a constant escapes the manifest audit of persisted PII surfaces.
 */

type WizardProgress = WizardPersistedSnapshot<PersonalizationStep>;

/**
 * Steps that cannot be re-entered cold, mapped to where a resumed session picks
 * the flow back up.
 *
 * Everything the user typed lives in `formData`, but the Learn Basics demo
 * (sample transcript, generated note, the edit tasks) and the template
 * generation screen exist only in component state — landing on them after a
 * refresh would show an empty preview or a spinner with no request behind it.
 * Resuming rewinds to the step that produces that state instead.
 */
const RESUME_STEP_OVERRIDES: Partial<Record<PersonalizationStep, PersonalizationStep>> = {
  'note-ready': 'mic-test',
  'note-edit': 'mic-test',
  'template-loading': 'template-creation',
};

/**
 * `TFormData` is the caller's form shape: the record is opaque JSON here, and
 * the app names its own `PersonalizationFormData` at each call site.
 */
export type OnboardingProgress<TFormData extends object = object> = {
  wizard: WizardProgress | null;
  /**
   * Partial by design: a record written before a form field was added or
   * removed still merges cleanly over the current defaults.
   */
  formData: Partial<TFormData> | null;
  /**
   * Whether the persisted country is the user's own pick rather than the
   * timezone/geo default. Stored separately because `formData.country` alone
   * can't tell them apart, and the in-memory ref that normally makes the
   * distinction is reset by the refresh.
   */
  hasUserSetCountry: boolean;
};

type StoredProgress<TFormData extends object = object> = OnboardingProgress<TFormData> & {
  flowKind: OnboardingFlowKind;
};

const isDeclaredStep = (value: unknown): value is PersonalizationStep =>
  typeof value === 'string' &&
  (personalizationWizard.stepIds as readonly string[]).includes(value);

const isFlowKind = (value: unknown): value is OnboardingFlowKind =>
  value === 'v2' || value === 'learn-basics';

const toWizardProgress = (value: unknown): WizardProgress | null => {
  if (!value || typeof value !== 'object') return null;

  const candidate = value as Record<string, unknown>;
  if (!isDeclaredStep(candidate.step)) return null;

  return {
    step: candidate.step,
    completed: Array.isArray(candidate.completed)
      ? candidate.completed.filter(isDeclaredStep)
      : [],
    history: Array.isArray(candidate.history)
      ? candidate.history.filter(isDeclaredStep)
      : [],
    done: candidate.done === true,
  };
};

/**
 * Apply the resume policy to a stored position. Returns `null` when there is
 * nothing to resume — no stored position, or a flow already finished.
 */
const toResumablePosition = (progress: WizardProgress | null): WizardProgress | null => {
  if (!progress || progress.done) return null;

  const resumeStep = RESUME_STEP_OVERRIDES[progress.step];
  if (!resumeStep) return progress;

  // Rewinding the step has to rewind the trail behind it too, or `back()` would
  // walk forward into the steps we just decided not to restore.
  const rewindIndex = progress.history.indexOf(resumeStep);
  const history = rewindIndex > 0 ? progress.history.slice(0, rewindIndex) : [];

  return {
    step: resumeStep,
    history,
    completed: progress.completed.filter((step) => history.includes(step)),
    done: false,
  };
};

const readStored = <
  TFormData extends object = object,
>(): StoredProgress<TFormData> | null => {
  if (typeof globalThis.sessionStorage === 'undefined') return null;

  try {
    const serialized = globalThis.sessionStorage.getItem(
      'heidi-onboarding:personalization-progress:v1'
    );
    if (!serialized) return null;

    const parsed: unknown = JSON.parse(serialized);
    if (!parsed || typeof parsed !== 'object') return null;

    const candidate = parsed as Record<string, unknown>;
    if (!isFlowKind(candidate.flowKind)) return null;

    return {
      flowKind: candidate.flowKind,
      wizard: toWizardProgress(candidate.wizard),
      formData:
        candidate.formData && typeof candidate.formData === 'object'
          ? (candidate.formData as Partial<TFormData>)
          : null,
      hasUserSetCountry: candidate.hasUserSetCountry === true,
    };
  } catch {
    return null;
  }
};

const write = (
  flowKind: OnboardingFlowKind,
  patch: Partial<OnboardingProgress>
): void => {
  if (typeof globalThis.sessionStorage === 'undefined') return;

  try {
    const existing = readStored();
    // A record from the other variant is replaced, not merged: the flows have
    // different entry steps and only partially overlapping fields.
    const base: StoredProgress =
      existing?.flowKind === flowKind
        ? existing
        : { flowKind, wizard: null, formData: null, hasUserSetCountry: false };

    globalThis.sessionStorage.setItem(
      'heidi-onboarding:personalization-progress:v1',
      JSON.stringify({ ...base, ...patch })
    );
  } catch {
    // Storage may be unavailable (private mode) or full — the flow still runs,
    // it just won't survive a refresh.
  }
};

/**
 * Read the resumable progress for `flowKind`, or `null` when there is none.
 * Call once per mount and use the same result to seed both the form state and
 * the wizard's `restore()`, so the step and the fields come from one record.
 */
export const readOnboardingProgress = <TFormData extends object = object>(
  flowKind: OnboardingFlowKind
): OnboardingProgress<TFormData> | null => {
  const stored = readStored<TFormData>();
  if (!stored || stored.flowKind !== flowKind) return null;

  const wizard = toResumablePosition(stored.wizard);
  if (!wizard && !stored.formData) return null;

  return {
    wizard,
    formData: stored.formData,
    hasUserSetCountry: stored.hasUserSetCountry,
  };
};

export const saveOnboardingWizardProgress = (
  flowKind: OnboardingFlowKind,
  wizard: WizardProgress
): void => write(flowKind, { wizard });

export const saveOnboardingFormData = <TFormData extends object>(
  flowKind: OnboardingFlowKind,
  formData: TFormData
): void => write(flowKind, { formData });

export const saveOnboardingCountryAsUserSet = (flowKind: OnboardingFlowKind): void =>
  write(flowKind, { hasUserSetCountry: true });

export const clearOnboardingProgress = (): void => {
  if (typeof globalThis.sessionStorage === 'undefined') return;

  try {
    globalThis.sessionStorage.removeItem('heidi-onboarding:personalization-progress:v1');
  } catch {
    // Nothing to recover from — the record expires with the tab regardless.
  }
};
